import {
  secretValueMaxLength,
  type SecretListing,
} from "@repo/app-types/entities/secret";
import { FieldError } from "@repo/ui/field/fieldError";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { useId, type FC } from "react";
import { Input, LabelContext, TextField } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { formatTypedSecretName } from "../formatTypedSecretName.js";

interface SecretFieldProps {
  /** The stored secret, whose name can't change, or `undefined` to create one. */
  secret: SecretListing | undefined;
  /** A new secret's name, formatted as it is typed; unused for a stored secret. */
  name: string;
  onNameChange: (name: string) => void;
  value: string;
  onValueChange: (value: string) => void;
  nameError?: string;
  valueError?: string;
}

// The inputs drop the @tailwindcss/forms chrome, so the box around them shows
// it. An input the error is about takes `Input`'s error text colors.
const inputClassName =
  "w-full min-w-0 border-none bg-transparent px-2.5 py-0 text-sm leading-5 text-inherit placeholder-gray-400 shadow-none ring-0 outline-hidden data-[invalid]:text-red-900 data-[invalid]:placeholder-red-400";

// Browsers and password managers would offer saved text and passwords here,
// and spellcheck would mark names; none of that fits a secret.
const noSuggestionsFieldProps = {
  autoComplete: "off",
  autoCorrect: "off",
  autoCapitalize: "off",
  spellCheck: "false",
} as const;
const noPasswordManagerInputProps = {
  "data-1p-ignore": true,
  "data-lpignore": "true",
  "data-bwignore": true,
  "data-form-type": "other",
} as const;

/**
 * A secret's name and value side by side in one box. A stored secret's name is
 * locked; a new secret's is typed.
 */
export const SecretField: FC<SecretFieldProps> = (props) => {
  const {
    secret,
    name,
    onNameChange,
    value,
    onValueChange,
    nameError,
    valueError,
  } = props;
  const labelId = useId();
  const nameId = useId();
  const nameInputId = useId();
  const valueInputId = useId();
  const messageId = useId();
  const errorMessage = nameError ?? valueError;

  const describedBy = errorMessage ? messageId : undefined;

  return (
    <div role="group" aria-labelledby={labelId} className="flex flex-col gap-2">
      {/* Clicking the label focuses the input to fill first. */}
      <LabelContext.Provider
        value={{ htmlFor: secret ? valueInputId : nameInputId }}
      >
        <Label id={labelId} size={Size.SMALL}>
          Secret
        </Label>
      </LabelContext.Provider>
      <div
        className={twMerge(
          // An outline rather than `Input`'s inset ring: it paints over the
          // locked name's background instead of under it.
          "flex min-h-8 w-full min-w-0 items-stretch overflow-hidden rounded-md bg-white text-sm leading-5 text-foreground shadow-xs outline-1 -outline-offset-1 outline-border-strong [&:focus-within]:outline-2 [&:focus-within]:-outline-offset-2 [&:focus-within]:outline-indigo-600",
          errorMessage &&
            "outline-2 -outline-offset-2 outline-red-500 [&:focus-within]:outline-red-500",
        )}
      >
        {secret ? (
          // A label, so clicking the locked name focuses the value input.
          <label
            id={nameId}
            htmlFor={valueInputId}
            className="flex max-w-2/5 shrink-0 items-center border-r border-border-strong bg-slate-50 px-2.5 font-mono text-muted-foreground"
          >
            <span className="min-w-0 truncate">{secret.name}</span>
          </label>
        ) : (
          <TextField
            id={nameInputId}
            aria-label="Secret name"
            aria-describedby={describedBy}
            className="flex w-2/5 shrink-0 border-r border-border-strong"
            value={name}
            onChange={(text) => onNameChange(formatTypedSecretName(text))}
            isInvalid={nameError !== undefined}
            autoFocus={name === ""}
            {...noSuggestionsFieldProps}
          >
            <Input
              {...noPasswordManagerInputProps}
              className={twMerge(inputClassName, "font-mono")}
              placeholder="API_KEY"
            />
          </TextField>
        )}
        <TextField
          id={valueInputId}
          aria-label={secret ? undefined : "Secret value"}
          aria-labelledby={secret ? `${labelId} ${nameId}` : undefined}
          aria-describedby={describedBy}
          className="flex min-w-0 flex-1"
          type="password"
          value={value}
          onChange={onValueChange}
          isInvalid={valueError !== undefined}
          // Autofocus acts only as the field mounts: a name chosen before then,
          // in the search, sends the focus on to the value.
          autoFocus={secret !== undefined || name !== ""}
          {...noSuggestionsFieldProps}
          maxLength={secretValueMaxLength}
        >
          {/* A stored value can't be read back; the dots say one is there. */}
          <Input
            {...noPasswordManagerInputProps}
            className={inputClassName}
            placeholder={
              secret ? `••••${secret.value_hint ?? ""}` : "pk_a73n2n…"
            }
          />
        </TextField>
      </div>
      {errorMessage ? (
        <FieldError id={messageId}>{errorMessage}</FieldError>
      ) : null}
    </div>
  );
};
